import { Inbox } from 'lucide-react';
import { useMemo } from 'react';

import type { Thread, ThreadTarget } from '../threads.schema';
import { ThreadItem } from './ThreadItem';

interface Props {
    threads: Thread[];
    target: ThreadTarget;
}

interface BuildResult {
    open: Thread[];
    resolved: Thread[];
    repliesByRoot: Map<number, Thread[]>;
    resolvedIssueIds: Set<number>;
}

function build(threads: Thread[]): BuildResult {
    const repliesByRoot = new Map<number, Thread[]>();
    const resolvedIssueIds = new Set<number>();
    const roots: Thread[] = [];

    for (const t of threads) {
        if (t.parentId != null) {
            const bucket = repliesByRoot.get(t.parentId);
            if (bucket) bucket.push(t);
            else repliesByRoot.set(t.parentId, [t]);
        } else {
            roots.push(t);
        }
        const resolves = t.params?.resolvesIssueId;
        if (resolves != null) resolvedIssueIds.add(Number(resolves));
    }

    const open: Thread[] = [];
    const resolved: Thread[] = [];
    for (const r of roots) {
        if (resolvedIssueIds.has(r.id)) resolved.push(r);
        else open.push(r);
    }

    // Newest first within each section so the freshest problem leads.
    const byNewest = (a: Thread, b: Thread) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    open.sort(byNewest);
    resolved.sort(byNewest);

    return { open, resolved, repliesByRoot, resolvedIssueIds };
}

export function ThreadTree({ threads, target }: Props) {
    const { open, resolved, repliesByRoot, resolvedIssueIds } = useMemo(() => build(threads), [threads]);

    if (threads.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-solid border-gray-300 bg-gray-50/50 p-10 text-center dark:border-gray-700 dark:bg-gray-900/40">
                <Inbox aria-hidden="true" className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                    Nicio problemă semnalată
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Scrie mai jos pentru a deschide prima problemă.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {open.map((root) => (
                <ThreadItem
                    key={root.id}
                    thread={root}
                    target={target}
                    replies={repliesByRoot.get(root.id) || []}
                    resolvedIssueIds={resolvedIssueIds}
                />
            ))}

            {resolved.length > 0 && (
                <div className="mt-2 flex items-center gap-3">
                    <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Rezolvate ({resolved.length})
                    </span>
                    <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                </div>
            )}

            {resolved.map((root) => (
                <ThreadItem
                    key={root.id}
                    thread={root}
                    target={target}
                    replies={repliesByRoot.get(root.id) || []}
                    resolvedIssueIds={resolvedIssueIds}
                />
            ))}
        </div>
    );
}
